// src/pages/Login.jsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setErro('');
    setCarregando(true);

    try {
      await login(email, senha);
      navigate('/');
    } catch (error) {
      if (error.message.includes('Invalid login credentials')) {
        setErro('E-mail ou senha incorretos.');
      } else if (error.message.includes('Email not confirmed')) {
        setErro('E-mail não confirmado. Verifique sua caixa de entrada.');
      } else {
        setErro('Erro ao entrar. Tente novamente.');
      }
    } finally {
      setCarregando(false);
    }
  }

  return (
    <div style={styles.page}>
      {/* Painel esquerdo — branding */}
      <div style={styles.branding}>
        <div style={styles.brandingInner}>
          <div style={styles.logoWrap}>
            <div style={styles.logoMark}>
              <span style={styles.logoV}>V</span>
            </div>
            <div>
              <div style={styles.logoName}>VULI GROUP</div>
              <div style={styles.logoTagline}>Inteligência que move resultados.</div>
            </div>
          </div>

          <div style={styles.brandingText}>
            <h1 style={styles.brandingH1}>Bem-vinda ao CRM VULI Group</h1>
            <p style={styles.brandingP}>
              Gerencie leads, projetos e metas da sua operação comercial em um único lugar.
            </p>
          </div>

          <div style={styles.features}>
            {[
              { icon: '◈', text: 'Leads e pipeline por projeto' },
              { icon: '◈', text: 'Casos, contratos e agenda Closer' },
              { icon: '◈', text: 'Metas com porquinho animado' },
              { icon: '◈', text: 'Integração ManyChat e Whaticket' },
            ].map((f, i) => (
              <div key={i} style={styles.featureItem}>
                <span style={styles.featureIcon}>{f.icon}</span>
                <span style={styles.featureText}>{f.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Painel direito — formulário */}
      <div style={styles.formPanel}>
        <div style={styles.formCard}>
          <div style={styles.formHeader}>
            <h2 style={styles.formTitle}>Acessar o CRM</h2>
            <p style={styles.formSub}>Entre com seu e-mail e senha cadastrados.</p>
          </div>

          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>E-mail</label>
              <div style={styles.inputWrap}>
                <span style={styles.inputIcon}>✉</span>
                <input
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  autoFocus
                  style={styles.input}
                />
              </div>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Senha</label>
              <div style={styles.inputWrap}>
                <span style={styles.inputIcon}>🔒</span>
                <input
                  type={mostrarSenha ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={senha}
                  onChange={e => setSenha(e.target.value)}
                  required
                  style={styles.input}
                />
                <button
                  type="button"
                  onClick={() => setMostrarSenha(!mostrarSenha)}
                  style={styles.toggleSenha}
                >
                  {mostrarSenha ? '🙈' : '👁'}
                </button>
              </div>
            </div>

            {erro && <div style={styles.erroBox}>⚠ {erro}</div>}

            <button
              type="submit"
              disabled={carregando}
              style={{ ...styles.btnEntrar, opacity: carregando ? 0.7 : 1, cursor: carregando ? 'not-allowed' : 'pointer' }}
            >
              {carregando ? 'Entrando...' : 'Entrar no CRM'}
            </button>

            <div style={styles.forgotWrap}>
              <button
                type="button"
                onClick={async () => {
                  if (!email) { setErro('Digite seu e-mail primeiro.'); return; }
                  const { supabase } = await import('../lib/supabase');
                  await supabase.auth.resetPasswordForEmail(email);
                  setErro('');
                  alert('E-mail de recuperação enviado! Verifique sua caixa de entrada.');
                }}
                style={styles.forgotLink}
              >
                Esqueceu a senha?
              </button>
            </div>
          </form>
        </div>

        <p style={styles.footer}>VULI GROUP · CRM Comercial · Acesso restrito</p>
      </div>
    </div>
  );
}

const styles = {
  page: { display:'flex', minHeight:'100vh', fontFamily:"'Poppins', sans-serif" },
  branding: { flex:1, background:'#3F0051', display:'flex', alignItems:'center', justifyContent:'center', padding:'40px', overflow:'hidden' },
  brandingInner: { maxWidth:'420px', width:'100%' },
  logoWrap: { display:'flex', alignItems:'center', gap:'14px', marginBottom:'48px' },
  logoMark: { width:'52px', height:'52px', borderRadius:'10px', background:'rgba(212,175,55,0.15)', border:'2px solid #D4AF37', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 },
  logoV: { fontFamily:"'Playfair Display', serif", fontSize:'26px', fontWeight:'700', color:'#D4AF37', lineHeight:1 },
  logoName: { fontSize:'16px', fontWeight:'700', color:'white', letterSpacing:'0.12em', lineHeight:1.1 },
  logoTagline: { fontSize:'10px', color:'#B58CC6', letterSpacing:'0.06em', marginTop:'2px' },
  brandingText: { marginBottom:'36px' },
  brandingH1: { fontFamily:"'Playfair Display', serif", fontSize:'28px', fontWeight:'500', color:'white', lineHeight:1.2, marginBottom:'16px' },
  brandingP: { fontSize:'13px', color:'#B58CC6', lineHeight:1.6, marginBottom:'36px' },
  features: { display:'flex', flexDirection:'column', gap:'12px' },
  featureItem: { display:'flex', alignItems:'center', gap:'12px' },
  featureIcon: { fontSize:'16px', color:'#D4AF37', flexShrink:0 },
  featureText: { fontSize:'13px', color:'rgba(255,255,255,0.7)', lineHeight:1.4 },
  formPanel: { width:'440px', flexShrink:0, background:'#FAF6F9', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'40px 32px' },
  formCard: { background:'white', borderRadius:'16px', border:'0.5px solid #e5e7eb', padding:'32px', width:'100%', maxWidth:'360px' },
  formHeader: { marginBottom:'28px' },
  formTitle: { fontFamily:"'Playfair Display', serif", fontSize:'22px', fontWeight:'500', color:'#3F0051', marginBottom:'6px' },
  formSub: { fontSize:'12px', color:'#9ca3af' },
  form: { display:'flex', flexDirection:'column', gap:'0' },
  inputGroup: { marginBottom:'18px' },
  label: { fontSize:'12px', fontWeight:'500', color:'#374151', display:'block', marginBottom:'6px' },
  inputWrap: { position:'relative', display:'flex', alignItems:'center' },
  inputIcon: { position:'absolute', left:'12px', fontSize:'13px', color:'#9ca3af', pointerEvents:'none' },
  input: { width:'100%', height:'42px', padding:'0 40px 0 36px', border:'1px solid #e5e7eb', borderRadius:'8px', fontSize:'13px', color:'#1f2937', background:'white', outline:'none', boxSizing:'border-box' },
  toggleSenha: { position:'absolute', right:'10px', background:'none', border:'none', cursor:'pointer', fontSize:'14px', padding:'4px' },
  erroBox: { background:'#fee2e2', color:'#991b1b', fontSize:'12px', padding:'10px 14px', borderRadius:'8px', marginBottom:'16px', border:'0.5px solid #fca5a5' },
  btnEntrar: { width:'100%', height:'44px', background:'#3F0051', color:'white', border:'none', borderRadius:'8px', fontSize:'14px', fontWeight:'600', cursor:'pointer', marginBottom:'16px' },
  forgotWrap: { textAlign:'center' },
  forgotLink: { fontSize:'12px', color:'#B58CC6', background:'none', border:'none', cursor:'pointer', fontWeight:'500' },
  footer: { marginTop:'24px', fontSize:'11px', color:'#9ca3af', textAlign:'center' },
};
